/**
 * Runs `fn` every `ms` while the page is visible. Hidden, it stops; visible again, it runs `fn` at once and resumes.
 * The document and timers are injectable so tests run without a browser.
 */
export interface VisibilitySource {
  readonly visibilityState: DocumentVisibilityState
  addEventListener(type: 'visibilitychange', fn: () => void): void
  removeEventListener(type: 'visibilitychange', fn: () => void): void
}

export interface Timers {
  setInterval: (fn: () => void, ms: number) => unknown
  clearInterval: (id: unknown) => void
}

const realTimers: Timers = {
  setInterval: (fn, ms) => setInterval(fn, ms),
  clearInterval: (id) => clearInterval(id as ReturnType<typeof setInterval>),
}

export function visibleInterval(fn: () => void, ms: number, doc: VisibilitySource = document, timers: Timers = realTimers): () => void {
  let id: unknown = null
  const start = () => {
    if (id === null) id = timers.setInterval(fn, ms)
  }
  const stop = () => {
    if (id !== null) timers.clearInterval(id)
    id = null
  }
  const onChange = () => {
    if (doc.visibilityState === 'visible') {
      fn()
      start()
    } else stop()
  }
  if (doc.visibilityState === 'visible') start()
  doc.addEventListener('visibilitychange', onChange)
  return () => {
    stop()
    doc.removeEventListener('visibilitychange', onChange)
  }
}
